/**
 * Gamepad.ts — gamepad input source (polled each frame via the Gamepad API).
 *
 * Left stick X steers; right trigger accelerates and left trigger brakes/reverses
 * (left stick Y is a fallback). The single use-item control (M12) is A, B or RB —
 * reported as a HELD boolean; the InputManager turns it into a one-shot "fire"
 * edge so a held button fires exactly once.
 *
 * Output is RAW screen-space (x = +right). The §6.2 player flip is applied later.
 */
export interface GamepadReading {
  throttle: number;
  steer: number;
  useItem: boolean; // A / B / RB
  pause: boolean; // Start
  active: boolean;
}

const DEADZONE = 0.15;

function deadzone(v: number): number {
  return Math.abs(v) < DEADZONE ? 0 : v;
}

export class Gamepad {
  read(): GamepadReading {
    const pads = navigator.getGamepads?.() ?? [];
    const pad = pads.find((p): p is globalThis.Gamepad => p !== null);
    if (!pad) {
      return { throttle: 0, steer: 0, useItem: false, pause: false, active: false };
    }

    const steer = deadzone(pad.axes[0] ?? 0);
    const rt = pad.buttons[7]?.value ?? 0; // right trigger -> accelerate
    const lt = pad.buttons[6]?.value ?? 0; // left trigger -> brake/reverse
    let throttle = rt - lt;
    if (Math.abs(throttle) < 0.02) throttle = -deadzone(pad.axes[1] ?? 0); // stick Y fallback

    const useItem =
      (pad.buttons[0]?.pressed ?? false) || // A (south)
      (pad.buttons[1]?.pressed ?? false) || // B (east)
      (pad.buttons[5]?.pressed ?? false); // RB
    const pause = pad.buttons[9]?.pressed ?? false; // Start

    const active = steer !== 0 || throttle !== 0 || useItem;
    return { throttle, steer, useItem, pause, active };
  }
}
